import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useTimeContext } from "../pages/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Minus, AlertTriangle, ShieldAlert, Building2, Target, Eye, ArrowRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line, ReferenceLine } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => { if (n == null) return '--'; const a = Math.abs(n); const s = n < 0 ? '-' : ''; if (a >= 1e7) return `${s}${(a/1e7).toFixed(2)} Cr`; if (a >= 1e5) return `${s}${(a/1e5).toFixed(2)} L`; if (a >= 1e3) return `${s}${(a/1e3).toFixed(1)}K`; return s + a.toLocaleString(); };
const SEV = { critical: 'border-red-400 bg-red-50', high: 'border-orange-400 bg-orange-50', medium: 'border-amber-300 bg-amber-50', low: 'border-emerald-300 bg-emerald-50' };
const SEV_DOT = { critical: 'bg-red-500', high: 'bg-orange-500', medium: 'bg-amber-500', low: 'bg-emerald-500' };
const TRAP_BG = (t) => t.is_trap ? (t.trend === 'declining' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200') : 'bg-slate-50 border-slate-100';

export default function CollectionAnalytics() {
  const { token } = useTimeContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/kpi/collection-analytics`, { headers: { Authorization: `Bearer ${token}` } }); setData(res.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API/axios are module constants, setters are stable
  }, [token]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const moneyTrapsWithOutstanding = useMemo(() =>
    (data?.money_traps || []).filter(t => t.outstanding > 0 || t.is_trap),
    [data?.money_traps]
  );

  const topOutstanding = useMemo(() =>
    (data?.money_traps || []).filter(t => t.outstanding > 0).slice(0, 5),
    [data?.money_traps]
  );

  if (loading) return <div className="text-center py-12"><div className="inline-block h-6 w-6 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin" /></div>;
  if (!data) return null;
  const d = data;
  const h = d.headline || {};
  const rvr = d.real_vs_reported || {};
  const proj = d.projection || {};

  return (
    <div className="space-y-5" data-testid="collection-analytics">

      {/* ═══ 1. THE HEADLINE ═══ */}
      <Card className="bg-gradient-to-br from-[#0F1D32] to-[#1a2d4a] rounded-2xl border-0 text-white shadow-lg">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-[10px] font-bold text-blue-300 uppercase tracking-[0.15em]">Collection Trend</p>
              <div className="flex items-center gap-3 mt-2">
                {h.direction === 'up' ? <TrendingUp className="h-8 w-8 text-emerald-400" /> : h.direction === 'down' ? <TrendingDown className="h-8 w-8 text-red-400" /> : <Minus className="h-8 w-8 text-slate-400" />}
                <div>
                  <p className="text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>{fmt(h.current_q_collected)}</p>
                  <p className="text-xs text-slate-400">{h.current_quarter}</p>
                </div>
                <div className="ml-4 px-3 py-1 rounded-lg bg-white/10">
                  <p className={`text-lg font-bold ${h.change_pct > 0 ? 'text-emerald-400' : h.change_pct < 0 ? 'text-red-400' : 'text-slate-300'}`}>{h.change_pct > 0 ? '+' : ''}{h.change_pct}%</p>
                  <p className="text-[9px] text-slate-400">vs {h.prev_quarter}</p>
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[9px] text-slate-400 uppercase">Efficiency</p>
              <p className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>{h.current_q_efficiency}%</p>
              <p className="text-[9px] text-slate-500">was {h.prev_q_efficiency}%</p>
            </div>
          </div>
          {h.why?.length > 0 && (
            <div className="pt-3 border-t border-white/10">
              <p className="text-[9px] text-slate-400 uppercase font-semibold mb-2">Biggest Movers This Quarter</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {h.why.map((w, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-white/5">
                    <span className="text-[10px] text-slate-300 truncate mr-2">{w.project}</span>
                    <span className={`text-[10px] font-bold ${w.change > 0 ? 'text-emerald-400' : 'text-red-400'}`}>{w.change > 0 ? '+' : ''}{fmt(w.change)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ═══ 5. THE ONE THING — Risk Signals ═══ */}
      {(d.risks || []).length > 0 && (
        <div className="space-y-2">
          {(d.risks || []).map((r, i) => (
            <div key={r.signal || `risk-${i}`} className={`flex items-start gap-3 p-4 rounded-xl border-l-4 ${SEV[r.severity]}`}>
              <div className={`h-2.5 w-2.5 rounded-full mt-1.5 flex-shrink-0 ${SEV_DOT[r.severity]}`} />
              <div className="flex-1">
                <p className="text-sm font-bold text-slate-800">{r.signal}</p>
                <p className="text-xs text-slate-600 mt-0.5">{r.detail}</p>
              </div>
              <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-lg flex-shrink-0 whitespace-nowrap">{r.action}</span>
            </div>
          ))}
        </div>
      )}

      {/* ═══ Monthly Trend Chart ═══ */}
      <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm">
        <CardHeader className="pb-2 border-b border-slate-100"><CardTitle className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>12-Month Demand vs Collection</CardTitle></CardHeader>
        <CardContent className="pt-4">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={d.monthly_trend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="month" tick={{ fontSize: 9 }} />
              <YAxis tick={{ fontSize: 9 }} tickFormatter={fmt} />
              <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '11px' }} formatter={v => fmt(v)} />
              <Legend wrapperStyle={{ fontSize: '10px' }} />
              <Bar dataKey="demanded" fill="#CBD5E1" name="Demanded" radius={[3, 3, 0, 0]} />
              <Bar dataKey="collected" fill="#3B82F6" name="Collected" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* ═══ 3. REAL vs REPORTED ═══ */}
      <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm">
        <CardHeader className="pb-2 border-b border-slate-100"><div className="flex items-center gap-2"><Eye className="h-4 w-4 text-indigo-500" /><CardTitle className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Real vs Reported — What the Numbers Don't Tell You</CardTitle></div></CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="p-3 bg-emerald-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-emerald-700" style={{ fontFamily: 'Manrope' }}>{rvr.reported_efficiency}%</p>
              <p className="text-[9px] text-emerald-600 font-semibold uppercase mt-1">Reported Efficiency</p>
              <p className="text-[8px] text-emerald-500 mt-0.5">Looks healthy</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-amber-700" style={{ fontFamily: 'Manrope' }}>{rvr.customer_coverage}%</p>
              <p className="text-[9px] text-amber-600 font-semibold uppercase mt-1">Customer Coverage</p>
              <p className="text-[8px] text-amber-500 mt-0.5">{rvr.fully_paid} of {rvr.total_customers} fully paid</p>
            </div>
            <div className="p-3 bg-red-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-red-700" style={{ fontFamily: 'Manrope' }}>{rvr.zero_collection}</p>
              <p className="text-[9px] text-red-600 font-semibold uppercase mt-1">Zero Collection</p>
              <p className="text-[8px] text-red-500 mt-0.5">{fmt(rvr.zero_collection_value)} demanded, ₹0 received</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Manrope' }}>{rvr.critical_90d}</p>
              <p className="text-[9px] text-slate-600 font-semibold uppercase mt-1">Critical (90d+)</p>
              <p className="text-[8px] text-slate-500 mt-0.5">{rvr.with_outstanding} total with outstanding</p>
            </div>
          </div>
          <div className="p-3 bg-slate-900 rounded-xl text-white text-center">
            <p className="text-xs">The headline says <b className="text-emerald-400">{rvr.reported_efficiency}% efficiency</b> — but only <b className="text-amber-400">{rvr.customer_coverage}%</b> of customers have fully paid. <b className="text-red-400">{rvr.zero_collection} customers</b> haven't paid a single rupee ({fmt(rvr.zero_collection_value)}).</p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* ═══ 4. PROJECTION ═══ */}
        <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm">
          <CardHeader className="pb-2 border-b border-slate-100"><div className="flex items-center gap-2"><ArrowRight className="h-4 w-4 text-blue-500" /><CardTitle className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Next Quarter Projection — {proj.next_quarter}</CardTitle></div></CardHeader>
          <CardContent className="pt-4">
            <p className="text-3xl font-bold text-blue-700 mb-3" style={{ fontFamily: 'Manrope' }}>{fmt(proj.total_projected)}</p>
            <div className="space-y-2">
              <div className="flex justify-between text-xs p-2 bg-blue-50 rounded-lg"><span className="text-blue-600">From known demands</span><span className="font-bold text-blue-700">{fmt(proj.projected_from_demands)}</span></div>
              <div className="flex justify-between text-xs p-2 bg-amber-50 rounded-lg"><span className="text-amber-600">Overdue recovery (est. 20%)</span><span className="font-bold text-amber-700">{fmt(proj.overdue_recovery)}</span></div>
              <div className="flex justify-between text-xs p-2 bg-slate-50 rounded-lg"><span className="text-slate-500">Known upcoming demands</span><span className="font-semibold">{fmt(proj.known_demands)}</span></div>
            </div>
            <p className="text-[9px] text-slate-400 mt-3">Based on current efficiency of {proj.efficiency_used}%. This quarter actual: {fmt(proj.current_q_actual)}.</p>
          </CardContent>
        </Card>

        {/* ═══ Outstanding Summary ═══ */}
        <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm">
          <CardHeader className="pb-2 border-b border-slate-100"><div className="flex items-center gap-2"><Target className="h-4 w-4 text-red-500" /><CardTitle className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Portfolio Outstanding</CardTitle></div></CardHeader>
          <CardContent className="pt-4">
            <p className="text-3xl font-bold text-red-600 mb-1" style={{ fontFamily: 'Manrope' }}>{fmt(d.total_outstanding)}</p>
            <p className="text-xs text-slate-500 mb-4">+ {fmt(d.total_interest)} interest accrued</p>
            <div className="space-y-2">
              {topOutstanding.map((t, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div className={`h-2 w-2 rounded-full ${t.trend === 'declining' ? 'bg-red-500' : t.trend === 'improving' ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                    <span className="text-slate-700 truncate max-w-[160px]">{t.project}</span>
                  </div>
                  <span className="font-bold text-red-600">{fmt(t.outstanding)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ═══ 2. MONEY TRAPS ═══ */}
      <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm">
        <CardHeader className="pb-2 border-b border-slate-100"><div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-amber-500" /><CardTitle className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Project-wise Collection Health</CardTitle></div></CardHeader>
        <CardContent className="pt-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {moneyTrapsWithOutstanding.map((t) => (
              <div key={t.project} className={`p-3 rounded-xl border ${TRAP_BG(t)}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-slate-800 truncate max-w-[140px]">{t.project}</span>
                  <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-full uppercase ${t.trend === 'declining' ? 'bg-red-100 text-red-700' : t.trend === 'improving' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{t.trend}</span>
                </div>
                <div className="flex justify-between text-[10px] mb-1">
                  <span className="text-slate-500">Outstanding</span>
                  <span className="font-bold text-red-600">{fmt(t.outstanding)}</span>
                </div>
                <div className="flex justify-between text-[10px] mb-1">
                  <span className="text-slate-500">Efficiency</span>
                  <span className={`font-bold ${t.efficiency >= 95 ? 'text-emerald-600' : t.efficiency >= 85 ? 'text-amber-600' : 'text-red-600'}`}>{t.efficiency}%</span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-slate-500">Stuck customers (60d+)</span>
                  <span className="font-bold text-slate-700">{t.stuck_customers}</span>
                </div>
                {t.is_trap && <p className="text-[8px] text-red-500 font-semibold mt-1.5 uppercase">Money Trap — below 95% efficiency</p>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
